import React, { useState } from 'react'
import { LogIn } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast.tsx'
import { describeTargetApp, startImpersonation } from '@/lib/impersonation'
import { cn } from '@/lib/utils'

interface ImpersonateButtonProps {
  /** The *user* id (not the profile id) of the student / counselor / agency admin. */
  userId?: string
  name: string
  isActive?: boolean
  /** Overrides the default "View as {name}" tooltip/aria label. */
  label?: string
  className?: string
}

/**
 * "View as this user" — super admin only. Renders nothing for anyone else, but
 * the real gate is the backend: POST /impersonation/start is super-admin-only.
 */
const ImpersonateButton: React.FC<ImpersonateButtonProps> = ({ userId, name, isActive = true, label: customLabel, className }) => {
  const { isSuperAdmin } = useAuth()
  const { showToast } = useToast()
  const [busy, setBusy] = useState(false)

  if (!isSuperAdmin || !userId) return null

  const disabled = busy || !isActive
  const label = isActive ? (customLabel ?? `View as ${name}`) : 'Inactive accounts cannot be impersonated'

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    setBusy(true)
    try {
      const result = await startImpersonation(userId)
      showToast({
        type: 'info',
        title: 'Impersonation started',
        description: `Opened ${result.targetName || name} in the ${describeTargetApp(result.targetApp)}. This session is logged.`,
      })
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Could not start impersonation',
        description: error instanceof Error ? error.message : 'Something went wrong',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cn(
        'p-1.5 rounded-md text-amber-600 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed',
        className,
      )}
    >
      <LogIn className="h-4 w-4" />
    </button>
  )
}

export default ImpersonateButton
